/**
 * CORRETOR ORTOGRÁFICO AUTOMÁTICO — exclusivamente ortográfico.
 *
 * Corrige grafia, acentuação, espaço duplicado, maiúscula inicial de frase e
 * erros evidentes de digitação em campos narrativos (comentários,
 * observações, parecer, recomendações, conclusão, motivo de HOLD…).
 *
 * NUNCA altera: TAGs, códigos, números, valores, unidades, fórmulas, normas e
 * suas edições, OS/Sub-OS, ZR, IDs de recomendação, lotes, certificados,
 * siglas técnicas, nomes de produtos/equipamentos, nem o sentido das frases.
 * Não melhora estilo, não resume, não expande, não troca termos técnicos.
 *
 * Determinístico e sem dependências: dicionário curado de erros comuns no
 * vocabulário de inspeção industrial + regras de espaço/pontuação/maiúscula.
 */

export type SpellingChangeKind = "GRAFIA" | "ACENTUAÇÃO" | "ESPAÇAMENTO" | "MAIÚSCULA";

export interface SpellingChange {
  kind: SpellingChangeKind;
  from: string;
  to: string;
  /** Posição no texto original. */
  index: number;
}

export interface SpellingResult {
  original: string;
  corrected: string;
  changed: boolean;
  changes: SpellingChange[];
  /** Tokens que o corretor reconheceu como técnicos e deixou intactos. */
  protectedTokens: string[];
}

export interface SpellingOptions {
  /** Corrige a primeira letra de cada frase (padrão: sim). */
  sentenceCase?: boolean;
  /** Normaliza espaços duplicados e espaço antes de pontuação (padrão: sim). */
  spacing?: boolean;
  /** Palavras adicionais protegidas (ex.: nomes próprios do cliente). */
  protect?: readonly string[];
}

/**
 * Erros de acentuação e grafia frequentes → forma correta. Só palavras
 * comuns e inequívocas: nada que, sem acento, seja outra palavra válida
 * ("esta/está", "e/é", "para/pára", "ate/até" ficam FORA de propósito).
 */
const DICTIONARY: Record<string, string> = {
  inspecao: "inspeção", inspecoes: "inspeções", corrosao: "corrosão", tubulacao: "tubulação", tubulacoes: "tubulações", manutencao: "manutenção",
  recomendacao: "recomendação", recomendacoes: "recomendações", verificacao: "verificação", avaliacao: "avaliação", medicao: "medição",
  medicoes: "medições", pressao: "pressão", pressoes: "pressões", oxidacao: "oxidação", condicao: "condição", condicoes: "condições",
  situacao: "situação", operacao: "operação", instalacao: "instalação", fabricacao: "fabricação", revisao: "revisão", revisoes: "revisões",
  conclusao: "conclusão", observacao: "observação", observacoes: "observações", execucao: "execução", protecao: "proteção", substituicao: "substituição",
  identificacao: "identificação", localizacao: "localização", utilizacao: "utilização", aplicacao: "aplicação", recuperacao: "recuperação",
  correcao: "correção", correcoes: "correções", aprovacao: "aprovação", reprovacao: "reprovação", calibracao: "calibração", vibracao: "vibração",
  deformacao: "deformação", degradacao: "degradação", infiltracao: "infiltração", informacao: "informação", informacoes: "informações",
  documentacao: "documentação", certificacao: "certificação", qualificacao: "qualificação", homologacao: "homologação", validacao: "validação",
  atencao: "atenção", funcao: "função", funcoes: "funções", opcao: "opção", secao: "seção", secoes: "seções", tensao: "tensão", tensoes: "tensões",
  extensao: "extensão", dimensao: "dimensão", dimensoes: "dimensões", expansao: "expansão", emissao: "emissão", transmissao: "transmissão",
  impressao: "impressão", posicao: "posição", conexao: "conexão", conexoes: "conexões", flexao: "flexão", erosao: "erosão", abrasao: "abrasão",
  cavitacao: "cavitação", indicacao: "indicação", indicacoes: "indicações", rejeicao: "rejeição", aceitacao: "aceitação", padrao: "padrão",
  padroes: "padrões", razao: "razão", vazao: "vazão", vazoes: "vazões", regiao: "região", regioes: "regiões", acoes: "ações", solucoes: "soluções",
  programacao: "programação", fixacao: "fixação", reparacao: "reparação", preparacao: "preparação", liberacao: "liberação", concentracao: "concentração",
  excecao: "exceção", excessao: "exceção", intervencao: "intervenção", prevencao: "prevenção", contencao: "contenção", retencao: "retenção",
  isolacao: "isolação", ventilacao: "ventilação", inclinacao: "inclinação", elevacao: "elevação", sustentacao: "sustentação", vedacao: "vedação",
  perfuracao: "perfuração", furacao: "furação", laminacao: "laminação", fundicao: "fundição", ocorrencia: "ocorrência", ocorrencias: "ocorrências",
  evidencia: "evidência", evidencias: "evidências", referencia: "referência", referencias: "referências", consequencia: "consequência",
  frequencia: "frequência", tendencia: "tendência", interferencia: "interferência", resistencia: "resistência", eficiencia: "eficiência",
  ausencia: "ausência", presenca: "presença", seguranca: "segurança", relatorio: "relatório", relatorios: "relatórios", laboratorio: "laboratório",
  historico: "histórico", criterio: "critério", criterios: "critérios", necessario: "necessário", necessaria: "necessária", necessarios: "necessários",
  obrigatorio: "obrigatório", obrigatoria: "obrigatória", temporario: "temporário", provisorio: "provisório", satisfatorio: "satisfatório",
  insatisfatorio: "insatisfatório", acessorio: "acessório", acessorios: "acessórios", usuario: "usuário", conteudo: "conteúdo", possivel: "possível",
  visivel: "visível", inoxidavel: "inoxidável", aplicavel: "aplicável", aceitavel: "aceitável", inaceitavel: "inaceitável", disponivel: "disponível",
  indisponivel: "indisponível", compativel: "compatível", incompativel: "incompatível", favoravel: "favorável", desfavoravel: "desfavorável",
  variavel: "variável", responsavel: "responsável", consideravel: "considerável", provavel: "provável", estavel: "estável", instavel: "instável",
  critico: "crítico", critica: "crítica", criticos: "críticos", criticas: "críticas", tecnico: "técnico", tecnica: "técnica", tecnicos: "técnicos",
  tecnicas: "técnicas", mecanico: "mecânico", mecanica: "mecânica", quimico: "químico", quimica: "química", periodico: "periódico",
  periodica: "periódica", proximo: "próximo", proxima: "próxima", ultimo: "último", ultima: "última", minimo: "mínimo", minima: "mínima",
  maximo: "máximo", maxima: "máxima", numero: "número", numeros: "números", orgao: "órgão", estagio: "estágio", diametro: "diâmetro",
  superficie: "superfície", liquido: "líquido", liquida: "líquida", particula: "partícula", particulas: "partículas", magnetica: "magnética",
  magnetico: "magnético", analise: "análise", analises: "análises", hipotese: "hipótese", nivel: "nível", niveis: "níveis", acumulo: "acúmulo",
  residuo: "resíduo", residuos: "resíduos", atmosferica: "atmosférica", atmosferico: "atmosférico", termica: "térmica", termico: "térmico",
  eletrica: "elétrica", eletrico: "elétrico", hidraulica: "hidráulica", hidraulico: "hidráulico", pneumatica: "pneumática", automatica: "automática",
  automatico: "automático", metalica: "metálica", metalico: "metálico", plastica: "plástica", ceramica: "cerâmica", dinamica: "dinâmica",
  estatica: "estática", especifica: "específica", especifico: "específico", unica: "única", unico: "único", rapido: "rápido", solido: "sólido",
  valido: "válido", invalido: "inválido", oleo: "óleo", agua: "água", acido: "ácido", hidrogenio: "hidrogênio", oxigenio: "oxigênio",
  nitrogenio: "nitrogênio", amonia: "amônia", valvula: "válvula", valvulas: "válvulas", berco: "berço", aluminio: "alumínio", titanio: "titânio",
  epoxi: "epóxi", aco: "aço", acos: "aços", area: "área", areas: "áreas", tambem: "também", porem: "porém", alem: "além", apos: "após", atraves: "através", entao: "então",
  senao: "senão", nao: "não", voce: "você", serao: "serão", estao: "estão", vacuo: "vácuo", conforne: "conforme", confome: "conforme",
  comforme: "conforme", relatoiro: "relatório", espesura: "espessura", espesuras: "espessuras", corossao: "corrosão", "corossão": "corrosão",
  necesario: "necessário", necessairo: "necessário", recomedacao: "recomendação", "recomedação": "recomendação", compoente: "componente",
  conponente: "componente", equipamneto: "equipamento", equipameto: "equipamento", equipamnetos: "equipamentos", isolameto: "isolamento",
  pinutra: "pintura", excesivo: "excessivo", exesso: "excesso", inpecao: "inspeção", "inpeção": "inspeção", "inspeçao": "inspeção",
  "inspecção": "inspeção", "tubulaçao": "tubulação", "verificaçao": "verificação", "identificaçao": "identificação", "avaliaçao": "avaliação",
  "mediçao": "medição", "recomendaçao": "recomendação", "observaçao": "observação", "correçao": "correção", "condiçao": "condição",
  mantencao: "manutenção", "mantenção": "manutenção", "manutençao": "manutenção", ocorencia: "ocorrência", "ocorrênica": "ocorrência",
  aprensenta: "apresenta", apresneta: "apresenta", aprensentou: "apresentou", identifcado: "identificado", identificdo: "identificado",
  verficado: "verificado", verificdo: "verificado", constatdo: "constatado", observdo: "observado", repado: "reparado", "substituíçao": "substituição",
  "substituiçao": "substituição", trica: "trinca", vazamnto: "vazamento", vazamneto: "vazamento", "defomação": "deformação", defomacao: "deformação",
  desgate: "desgaste", ferrugen: "ferrugem", "oxidaçao": "oxidação", "execuçao": "execução", "proteçao": "proteção", limpesa: "limpeza",
  vizualmente: "visualmente", vizual: "visual", vizuais: "visuais", parde: "parede", extena: "externa", intena: "interna", sodla: "solda",
  cordao: "cordão", cordoes: "cordões", flanje: "flange", ancorage: "ancoragem", imedita: "imediata", imedito: "imediato", urgencia: "urgência",
  emergencia: "emergência"
};

/** Palavras que NÃO devem ser tocadas mesmo que apareçam em minúsculas (unidades, siglas, termos técnicos). */
const PROTECTED_WORDS = new Set<string>([
  "mm", "cm", "m", "km", "pol", "in", "ft", "mpa", "kpa", "gpa", "psi", "bar", "kgf", "kgf/cm²", "kgf/cm2", "n", "kn", "ºc", "°c", "k",
  "hz", "khz", "mhz", "db", "mm/ano", "mpy", "rpm", "l", "ml", "kg", "g", "t", "s", "min", "h", "ano", "anos", "mês", "meses",
  "api", "asme", "astm", "nace", "iso", "abnt", "nbr", "nr", "aws", "en", "din", "iec", "ieee", "petrobras", "sae", "ansi", "asnt",
  "lp", "pm", "me", "us", "usc", "ri", "avr", "rdo", "zr", "os", "plh", "tc", "vr", "end", "ndt", "b-scan", "bscan", "c-scan", "tofd", "paut",
  "api570", "api510", "api653", "api580", "api581", "b31.3", "b31.1", "b31.4", "b31.8", "viii", "ix", "v", "ii", "iii", "iv", "vi", "vii",
  "tag", "tags", "rev", "id", "ids", "nº", "n°", "ok", "n/a", "sch", "dn", "nps", "od", "ext", "int",
  "epóxi", "epoxi", "primer", "zinco", "inox", "cs", "ss", "ltcs", "a516", "a106", "a53", "a105", "a234", "a333", "a335", "a312", "tp304", "tp316"
]);

const SENTENCE_END = /[.!?]\s+$/;

interface Token {
  text: string;
  index: number;
  isWord: boolean;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  // Palavras (com acentos, hífen, ponto interno e ²/³), espaços e o resto (pontuação) — nada se perde na junção.
  const re = /[\p{L}\p{N}][\p{L}\p{N}\-_./ºª°²³]*|\s+|[^\s\p{L}\p{N}]+/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const t = m[0];
    tokens.push({ text: t, index: m.index, isWord: /^[\p{L}\p{N}]/u.test(t) });
  }
  return tokens;
}

/** Token técnico? (dígitos, siglas em maiúsculas, hífen/ponto internos com números, unidades, normas…) — intocável. */
export function isProtectedToken(token: string, extra?: ReadonlySet<string>): boolean {
  const t = token.trim();
  if (!t) return false;
  const lower = t.replace(/[.,;:!?]+$/, "").toLowerCase();
  if (PROTECTED_WORDS.has(lower) || (extra && extra.has(lower))) return true;
  if (/\p{N}/u.test(t)) return true;                       // qualquer número/valor/código/TAG/lote/certificado/edição de norma
  if (/[-_/.]/.test(t.replace(/[.,;:!?]+$/, ""))) return true; // TK-101, P-1201A, A-516, B31.3, ZR-0001, kgf/cm²
  if (/^[\p{Lu}][\p{Lu}\p{N}º°]+$/u.test(t) && t.length >= 2) return true; // SIGLAS em maiúsculas (API, ASME, END, TAG)
  if (/^[\p{Lu}]\p{Ll}*[\p{Lu}]/u.test(t)) return true;    // CamelCase / marcas (McMaster, iPhone)
  if (/[@:#%§&*=<>{}[\]\\|~^]/.test(t)) return true;      // e-mails, urls, fórmulas
  return false;
}

function applyCase(corrected: string, original: string): string {
  if (original === original.toUpperCase() && original !== original.toLowerCase()) return corrected.toUpperCase();
  const first = original.charAt(0);
  if (first === first.toUpperCase() && first !== first.toLowerCase()) return corrected.charAt(0).toUpperCase() + corrected.slice(1);
  return corrected;
}

/** Se a diferença é só de acentos, a alteração conta como ACENTUAÇÃO; senão, GRAFIA. */
function kindOf(from: string, to: string): SpellingChangeKind {
  const strip = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  return strip(from) === strip(to) ? "ACENTUAÇÃO" : "GRAFIA";
}

/**
 * Corrige um texto narrativo. Retorna o original, o corrigido e cada
 * alteração (para o modal "CORREÇÕES ORTOGRÁFICAS ENCONTRADAS" e para a
 * auditoria). Texto vazio ou sem erros → `changed: false`.
 */
export function correctSpelling(text: string, options: SpellingOptions = {}): SpellingResult {
  const original = text ?? "";
  const sentenceCase = options.sentenceCase !== false;
  const spacing = options.spacing !== false;
  const extra = options.protect ? new Set(options.protect.map(w => w.toLowerCase())) : undefined;
  const changes: SpellingChange[] = [];
  const protectedTokens: string[] = [];
  if (!original.trim()) return { original, corrected: original, changed: false, changes, protectedTokens };

  const tokens = tokenize(original);
  const out: string[] = [];
  let atSentenceStart = true;
  let previousWasSpace = false;

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i]!;
    let text = tok.text;

    if (!tok.isWord) {
      if (/^\s+$/.test(text)) {
        // ESPAÇAMENTO: espaços duplicados → um (quebras de linha preservadas).
        if (spacing && !text.includes("\n") && text.length > 1) {
          changes.push({ kind: "ESPAÇAMENTO", from: text, to: " ", index: tok.index });
          text = " ";
        }
        previousWasSpace = true;
        out.push(text);
        continue;
      }
      // ESPAÇAMENTO: espaço antes de , . ; : ! ? → removido.
      if (spacing && previousWasSpace && /^[,.;:!?]/.test(text) && out.length > 0 && /^\s+$/.test(out[out.length - 1]!) && !out[out.length - 1]!.includes("\n")) {
        changes.push({ kind: "ESPAÇAMENTO", from: `${out[out.length - 1]}${text.charAt(0)}`, to: text.charAt(0), index: tok.index - 1 });
        out.pop();
      }
      previousWasSpace = false;
      if (/[.!?]/.test(text)) atSentenceStart = true;
      out.push(text);
      continue;
    }

    previousWasSpace = false;
    if (isProtectedToken(text, extra)) {
      protectedTokens.push(text);
      out.push(text);
      atSentenceStart = /[.!?]$/.test(text);
      continue;
    }

    // Só palavras "puras" (letras) passam pelo dicionário; pontuação final fica de fora da busca.
    const match = /^([\p{L}]+)([.,;:!?]*)$/u.exec(text);
    if (match) {
      const word = match[1]!;
      const tail = match[2] || "";
      const lower = word.toLowerCase();
      const replacement = DICTIONARY[lower];
      if (replacement && replacement !== lower) {
        const to = applyCase(replacement, word);
        changes.push({ kind: kindOf(word, to), from: word, to, index: tok.index });
        text = to + tail;
      }
      // MAIÚSCULA: início de frase em minúscula → maiúscula (só palavra alfabética não protegida).
      if (sentenceCase && atSentenceStart) {
        const first = text.charAt(0);
        if (first === first.toLowerCase() && first !== first.toUpperCase()) {
          const to = first.toUpperCase() + text.slice(1);
          changes.push({ kind: "MAIÚSCULA", from: text, to, index: tok.index });
          text = to;
        }
      }
    }
    atSentenceStart = /[.!?]$/.test(text);
    out.push(text);
  }

  const corrected = out.join("");
  // Frase iniciada depois de ". " sem que a pontuação fosse token isolado (ex.: "ok.proximo") já foi coberta pelo fluxo acima.
  if (corrected === original) return { original, corrected: original, changed: false, changes: [], protectedTokens };
  return { original, corrected, changed: true, changes, protectedTokens };
}

/** Campos narrativos por documento — só estes passam pelo corretor. Selects, números, datas, IDs, TAGs, normas e certificados nunca. */
export interface NarrativeField {
  field: string;
  label: string;
  value: string;
}

export interface NarrativeFieldSuggestion extends NarrativeField {
  result: SpellingResult;
}

/** Revisa vários campos de uma vez; devolve só os que têm correção. */
export function reviewNarrativeFields(fields: readonly NarrativeField[], options: SpellingOptions = {}): NarrativeFieldSuggestion[] {
  return fields
    .map(f => ({ ...f, result: correctSpelling(f.value || "", options) }))
    .filter(f => f.result.changed);
}

/** Alterações simples e inequívocas que podem ser aplicadas sem confirmação (configuração padrão: só espaçamento). */
export function isAutoApplicable(result: SpellingResult, kinds: readonly SpellingChangeKind[] = ["ESPAÇAMENTO"]): boolean {
  return result.changed && result.changes.every(c => kinds.includes(c.kind));
}

